/**
 * ChronologyRefineDrawer — "Refine with Arc" for one chronology entry.
 *
 * A right-side drawer holding an entry-scoped Arc conversation (one resumable
 * conversation per entry — `/refine/conversation` get-or-create). Turns go
 * through the standard chat pipeline with `chronology_refine_target` in the
 * body, which force-activates the chronology_refine toolkit and injects a
 * per-turn card snapshot server-side. Arc's `propose_entry_refinement` result
 * renders as an EntryRefinementCard diff (via ChatMessage.TOOL_CARDS); Apply
 * goes through the deterministic `/refine/apply` endpoint here — Arc never
 * writes the card.
 *
 * In-flow aside + backdrop (NOT a portal — z-index battles; see the React 18
 * portal lesson). Polling via useChatEvents, mirroring ExplorationTab.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import useChatEvents from '../hooks/useChatEvents';
import useHelpTarget from '../hooks/useHelpTarget';
import ChatMessage from './ChatMessage';
import { ChronologyRefineContext } from './chronologyRefineContext';
import { AGENT_NAME_SHORT } from '../brand';

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(20, 24, 40, 0.28)', zIndex: 980 },
  drawer: {
    position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 92vw)',
    background: '#fff', zIndex: 981, display: 'flex', flexDirection: 'column',
    boxShadow: '-8px 0 28px rgba(0,0,0,0.18)', borderLeft: '1px solid #e6e9ef',
  },
  head: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid #e6e9ef', background: '#f6f4fc' },
  title: { fontWeight: 700, fontSize: 14 },
  close: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#667', padding: 4 },
  startOver: { padding: '4px 10px', borderRadius: 6, border: '1px solid #c7ccd6', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#556' },
  cardSnap: { padding: '10px 14px', borderBottom: '1px solid #eee6fb', background: '#faf8ff', fontSize: 12.5 },
  snapTitle: { fontWeight: 600, fontSize: 13 },
  snapMeta: { color: '#778', marginTop: 2 },
  body: { flex: 1, overflowY: 'auto', padding: '10px 14px' },
  progressLine: { fontSize: 12, color: '#8a7fae', padding: '2px 0' },
  streamText: { fontSize: 13, whiteSpace: 'pre-wrap', padding: '6px 0' },
  err: { color: '#c0392b', fontSize: 13, padding: '6px 0' },
  composer: { display: 'flex', gap: 8, padding: '10px 14px', borderTop: '1px solid #e6e9ef', background: '#fafbfc' },
  input: { flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #c7ccd6', fontSize: 13, resize: 'none', fontFamily: 'inherit' },
  send: { padding: '8px 14px', borderRadius: 8, border: '1px solid #5b3fa8', background: '#5b3fa8', color: '#fff', cursor: 'pointer', fontSize: 13 },
  stop: { padding: '8px 14px', borderRadius: 8, border: '1px solid #c0392b', background: '#fff', color: '#c0392b', cursor: 'pointer', fontSize: 13 },
  emptyHint: { color: '#778', fontSize: 13, padding: '18px 4px', lineHeight: 1.5 },
};

/* Minimal event fold for a refine turn — the subset of ExplorationTab's
 * processEvents this drawer renders (text, tool trace, progress, done/error). */
function processEvents(events) {
  let text = '';
  const toolCalls = [];
  const toolResults = [];
  const progress = [];
  let done = null;
  let error = null;
  for (const evt of events) {
    const t = evt.event_type || evt.type;
    const d = evt.data ?? evt;
    switch (t) {
      case 'streaming_text':
        text += d.text ?? d.chunk ?? '';
        break;
      case 'tool_call':
        toolCalls.push({ tool_name: d.tool_name ?? d.name ?? d.tool, args: d.args ?? {}, sequence: evt.sequence });
        break;
      case 'tool_result':
        toolResults.push({
          tool_name: d.tool_name ?? d.name ?? d.tool,
          result: d.result ?? d.output,
          duration_ms: d.duration_ms,
          sequence: evt.sequence,
        });
        break;
      case 'progress': {
        const msg = d.message ?? d.text ?? d.finding ?? d.step_name;
        if (msg) {
          const last = progress[progress.length - 1];
          if (!last || last !== msg) progress.push(msg);
        }
        break;
      }
      case 'done':
        done = d;
        break;
      case 'error':
        error = d.error ?? d.message ?? 'Unknown error';
        break;
      default:
        break;
    }
  }
  const trace = toolCalls.map((tc) => {
    const tr = toolResults.find((r) => r.tool_name === tc.tool_name && r.sequence > tc.sequence);
    return { ...tc, result: tr?.result, duration_ms: tr?.duration_ms };
  });
  return { text, trace, progress, done, error };
}

export default function ChronologyRefineDrawer({
  caseId, chronologyId, entry, onClose, onApplied,
}) {
  const { token } = useAuth();
  const entryId = entry?.id || null;

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConv, setLoadingConv] = useState(true);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [streamProgress, setStreamProgress] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [appliedAt, setAppliedAt] = useState({});
  // Bumping this re-runs the open effect with fresh=true ("Start over").
  const [freshNonce, setFreshNonce] = useState(0);

  const allEventsRef = useRef([]);
  const seqRef = useRef(0);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const finishTurn = useCallback((state) => {
    const d = state.done || {};
    const content = d.answer ?? state.text;
    if (content || (d.tool_trace || state.trace).length) {
      setMessages((ms) => [...ms, {
        role: 'assistant',
        content: content || '',
        tool_trace: d.tool_trace ?? state.trace,
        citations: d.citations,
        partial: d.partial || d.status === 'cancelled' || d.status === 'budget_exceeded',
      }]);
    }
    setStreaming(false);
    setStreamText('');
    setStreamProgress([]);
    allEventsRef.current = [];
  }, []);

  const onEvents = useCallback((events) => {
    // Advance the drawer's sequence cursor. Without this, every send()
    // restarted polling at seq 0, replaying the WHOLE conversation — the
    // fold saw the previous turn's `done` instantly and re-appended the
    // previous reply as the new turn's answer (the "Arc repeats itself
    // before conceding" report: the repeat was a replayed old message,
    // the "concession" was the real reply landing afterwards).
    for (const e of events) {
      const seq = e.sequence ?? 0;
      if (seq > seqRef.current) seqRef.current = seq;
    }
    allEventsRef.current = [...allEventsRef.current, ...events];
    const state = processEvents(allEventsRef.current);
    setStreamText(state.text);
    setStreamProgress(state.progress.slice(-4));
    if (state.error) {
      setError(state.error);
      setStreaming(false);
      allEventsRef.current = [];
      return;
    }
    if (state.done) finishTurn(state);
  }, [finishTurn]);

  const onStatusChange = useCallback((status) => {
    if (status === 'completed' || status === 'interrupted' || status === 'failed') {
      // Belt-and-braces: fold whatever streamed if no done event arrived.
      if (allEventsRef.current.length) finishTurn(processEvents(allEventsRef.current));
      else setStreaming(false);
    }
  }, [finishTurn]);

  const chatEvents = useChatEvents(caseId, conversationId, {
    getToken: () => token, onEvents, onStatusChange, intervalMs: 500,
  });
  const chatEventsRef = useRef(chatEvents);
  useEffect(() => { chatEventsRef.current = chatEvents; }, [chatEvents]);

  // Open the entry's conversation — FRESH each time the drawer opens (or
  // Start over is clicked): the server archives the previous conversation
  // (kept in the case's conversation history) unless it's mid-stream, in
  // which case we resume it. Deps are the identity of the scoped entry +
  // the fresh nonce only — NOT the state this effect sets (the self-cancel
  // load-hang lesson).
  useEffect(() => {
    if (!entryId) return undefined;
    let cancelled = false;
    setLoadingConv(true);
    setConversationId(null);
    setMessages([]);
    setError('');
    setStreaming(false);
    allEventsRef.current = [];
    (async () => {
      try {
        const conv = await api.post(
          `/cases/${caseId}/chronologies/${chronologyId}/entries/${entryId}/refine/conversation`,
          { fresh: true },
        );
        if (cancelled) return;
        setConversationId(conv.conversation_id);
        seqRef.current = 0;
        if (conv.resumed_streaming) {
          // A turn is still running on the previous conversation — load its
          // history and pick up the stream where it left off.
          const detail = await api.get(
            `/exploration/case/${caseId}/conversations/${conv.conversation_id}`,
          );
          if (cancelled) return;
          setMessages((detail.messages || []).map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            tool_trace: m.tool_trace,
            citations: m.citations,
          })));
          seqRef.current = detail.last_event_seq || 0;
          setStreaming(true);
          chatEventsRef.current.start(seqRef.current, conv.conversation_id);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to open the refine conversation');
      } finally {
        if (!cancelled) setLoadingConv(false);
      }
    })();
    return () => { cancelled = true; chatEventsRef.current.stop(); };
  }, [caseId, chronologyId, entryId, freshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamText]);

  useEffect(() => {
    if (!streaming && !loadingConv) inputRef.current?.focus();
  }, [streaming, loadingConv]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming || !conversationId) return;
    setMessages((ms) => [...ms, { role: 'user', content: text }]);
    setInput('');
    setError('');
    setStreaming(true);
    setStreamText('');
    setStreamProgress([]);
    allEventsRef.current = [];
    try {
      await api.post(`/exploration/case/${caseId}/chat/start`, {
        message: text,
        conversation_id: conversationId,
        chronology_refine_target: { chronology_id: chronologyId, entry_id: entryId },
      });
      chatEventsRef.current.start(seqRef.current, conversationId);
    } catch (err) {
      setError(err.message || 'Failed to send');
      setStreaming(false);
    }
  };

  const stop = async () => {
    try {
      await api.post(`/exploration/case/${caseId}/chat/cancel/${conversationId}`);
    } catch { /* the poll loop surfaces the final state */ }
  };

  const apply = useCallback(async (payload, proposalKey) => {
    setBusy(true);
    try {
      await api.post(
        `/cases/${caseId}/chronologies/${chronologyId}/entries/${entryId}/refine/apply`,
        { ...payload, conversation_id: conversationId },
      );
      setAppliedAt((a) => ({ ...a, [proposalKey]: new Date().toISOString() }));
      onApplied?.();
    } finally {
      setBusy(false);
    }
  }, [caseId, chronologyId, entryId, conversationId, onApplied]);

  // Split (EntrySplitCard "Apply") → deterministic split endpoint; the
  // server re-verifies part date quotes against the entry's sources.
  const applySplit = useCallback(async (parts, keepOriginal, proposalKey) => {
    setBusy(true);
    try {
      await api.post(
        `/cases/${caseId}/chronologies/${chronologyId}/entries/${entryId}/split`,
        { parts, keep_original: !!keepOriginal },
      );
      setAppliedAt((a) => ({ ...a, [proposalKey]: new Date().toISOString() }));
      onApplied?.();
    } finally {
      setBusy(false);
    }
  }, [caseId, chronologyId, entryId, onApplied]);

  const refineCtx = useMemo(
    () => ({ apply, applySplit, appliedAt, busy, entryId }),
    [apply, applySplit, appliedAt, busy, entryId],
  );

  const helpRef = useHelpTarget({
    slug: 'refining-an-entry-with-arc',
    label: 'Refine with Arc',
    question: 'How do I refine a chronology entry with Arc and apply its proposed changes?',
  });

  if (!entry) return null;
  const dateLabel = entry.effective_date
    ? String(entry.effective_date).slice(0, 10)
    : `Undated${(entry.suggested_dates || []).length ? ` · ${entry.suggested_dates.length} candidate date(s)` : ''}`;

  return (
    <ChronologyRefineContext.Provider value={refineCtx}>
      <div style={S.backdrop} onClick={onClose} />
      <aside style={S.drawer} role="dialog" aria-label={`Refine with ${AGENT_NAME_SHORT}`} ref={helpRef}>
        <div style={S.head}>
          <span style={S.title}>✨ Refine with {AGENT_NAME_SHORT}</span>
          <span style={{ flex: 1 }} />
          {messages.length > 0 && !streaming && (
            <button
              style={S.startOver}
              onClick={() => setFreshNonce((n) => n + 1)}
              title="Start a new conversation about this entry (the old one moves to the case's conversation history)"
            >⟳ Start over</button>
          )}
          <button style={S.close} onClick={onClose} title="Close">✕</button>
        </div>

        {/* Pinned card snapshot — refreshed by the parent after each apply. */}
        <div style={S.cardSnap}>
          <div style={S.snapTitle}>{entry.title || '(untitled event)'}</div>
          <div style={S.snapMeta}>
            {dateLabel}
            {entry.enrichment?.summary && <> · Arc notes{entry.enrichment.dismissed_at ? ' (archived)' : ''}</>}
          </div>
          {entry.description && (
            <div style={{ ...S.snapMeta, color: '#556' }}>
              {String(entry.description).length > 180
                ? `${String(entry.description).slice(0, 179)}…`
                : entry.description}
            </div>
          )}
        </div>

        <div style={S.body}>
          {loadingConv && <div style={S.emptyHint}>Opening the conversation…</div>}
          {!loadingConv && messages.length === 0 && !streaming && (
            <div style={S.emptyHint}>
              Talk to {AGENT_NAME_SHORT} about this event — fix the date, tighten the
              description, add who was there, or ask it to re-read the sources.
              When you’re ready, ask it to propose the change: you’ll get a diff
              to review and apply. Nothing touches the card until you apply.
            </div>
          )}
          {messages.map((m, i) => (
            <ChatMessage key={m.id || `m-${i}`} msg={m} filenameMap={{}} token={token} />
          ))}
          {streaming && (
            <div>
              {streamProgress.map((p, i) => <div key={i} style={S.progressLine}>{p}</div>)}
              {streamText
                ? <div style={S.streamText}>{streamText}</div>
                : <div style={S.progressLine}>{AGENT_NAME_SHORT} is working…</div>}
            </div>
          )}
          {error && <div style={S.err}>{error}</div>}
          <div ref={endRef} />
        </div>

        <form style={S.composer} onSubmit={send}>
          <textarea
            ref={inputRef}
            style={S.input}
            rows={2}
            placeholder={`Refine this event with ${AGENT_NAME_SHORT}…`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            disabled={loadingConv}
          />
          {streaming
            ? <button type="button" style={S.stop} onClick={stop}>Stop</button>
            : <button type="submit" style={S.send} disabled={loadingConv || !input.trim()}>Send</button>}
        </form>
      </aside>
    </ChronologyRefineContext.Provider>
  );
}
